/* Dial codes, split out of AllJourneys.tsx on 2026-09-14 — and not for tidiness.
 * That file was 603 KB and the preview host serves source with a 256 KB cap, so
 * the extra ~7 KB of country rows pushed it past a boundary and the served copy
 * came back TRUNCATED: esbuild then failed on the cut, reporting a syntax error
 * at a line that is perfectly well-formed on disk. A long data table is the
 * right thing to move out first — no dependencies, one consumer.
 */
/* ── dial codes ──────────────────────────────────────────────────────────
 * Every country, not thirteen (operator, 2026-09-14). The list held US, CA, MX,
 * GB, DE, FR, IN, CN, BR, AU, NG and JP — so a visitor from anywhere else could
 * not pick their own country, and `parsePhone` could not recognise their `+`
 * prefix either: a typed +41 fell through to the default and was validated
 * against US rules.
 *
 * THREE COLUMNS, NOT FIVE. The flag is DERIVED from the ISO code (regional
 * indicator symbols), so 240 hardcoded surrogate pairs cannot drift from their
 * country. Length bounds: the twelve curated pairs the list already carried are
 * kept exactly; every other country gets the E.164 bound — at most 15 digits
 * including the country code, at least 4 — which is a real rule rather than a
 * per-country figure invented here. A stricter table is a data import, not a
 * guess to make in this file.
 *
 * `+1` resolves to US because it is first, exactly as before; the other NANP
 * members are their own rows and selectable by name.
 */
export const DIAL_EXACT: Record<string, [number, number]> = {
  US: [10, 10], CA: [10, 10], MX: [10, 10], GB: [10, 10], DE: [10, 11], FR: [9, 9],
  IN: [10, 10], CN: [11, 11], BR: [10, 11], AU: [9, 9], NG: [10, 10], JP: [10, 10],
};
export function flagOf(iso: string): string {
  try {
    return String.fromCodePoint(...iso.toUpperCase().split("").map((c) => 0x1F1E6 + c.charCodeAt(0) - 65));
  } catch { return ""; }
}
export const DIAL_RAW: [string, string, string][] = [
  ["US", "1", "United States"], ["CA", "1", "Canada"], ["GB", "44", "United Kingdom"],
  ["AF", "93", "Afghanistan"], ["AL", "355", "Albania"], ["DZ", "213", "Algeria"],
  ["AS", "1", "American Samoa"], ["AD", "376", "Andorra"], ["AO", "244", "Angola"],
  ["AI", "1", "Anguilla"], ["AG", "1", "Antigua and Barbuda"], ["AR", "54", "Argentina"],
  ["AM", "374", "Armenia"], ["AW", "297", "Aruba"], ["AU", "61", "Australia"],
  ["AT", "43", "Austria"], ["AZ", "994", "Azerbaijan"], ["BS", "1", "Bahamas"],
  ["BH", "973", "Bahrain"], ["BD", "880", "Bangladesh"], ["BB", "1", "Barbados"],
  ["BY", "375", "Belarus"], ["BE", "32", "Belgium"], ["BZ", "501", "Belize"],
  ["BJ", "229", "Benin"], ["BM", "1", "Bermuda"], ["BT", "975", "Bhutan"],
  ["BO", "591", "Bolivia"], ["BA", "387", "Bosnia and Herzegovina"], ["BW", "267", "Botswana"],
  ["BR", "55", "Brazil"], ["BN", "673", "Brunei"], ["BG", "359", "Bulgaria"],
  ["BF", "226", "Burkina Faso"], ["BI", "257", "Burundi"], ["KH", "855", "Cambodia"],
  ["CM", "237", "Cameroon"], ["CV", "238", "Cape Verde"], ["KY", "1", "Cayman Islands"],
  ["CF", "236", "Central African Republic"], ["TD", "235", "Chad"], ["CL", "56", "Chile"],
  ["CN", "86", "China"], ["CO", "57", "Colombia"], ["KM", "269", "Comoros"],
  ["CG", "242", "Congo – Brazzaville"], ["CD", "243", "Congo – Kinshasa"], ["CK", "682", "Cook Islands"],
  ["CR", "506", "Costa Rica"], ["CI", "225", "Côte d’Ivoire"], ["HR", "385", "Croatia"],
  ["CU", "53", "Cuba"], ["CW", "599", "Curaçao"], ["CY", "357", "Cyprus"],
  ["CZ", "420", "Czechia"], ["DK", "45", "Denmark"], ["DJ", "253", "Djibouti"],
  ["DM", "1", "Dominica"], ["DO", "1", "Dominican Republic"], ["EC", "593", "Ecuador"],
  ["EG", "20", "Egypt"], ["SV", "503", "El Salvador"], ["GQ", "240", "Equatorial Guinea"],
  ["ER", "291", "Eritrea"], ["EE", "372", "Estonia"], ["SZ", "268", "Eswatini"],
  ["ET", "251", "Ethiopia"], ["FO", "298", "Faroe Islands"], ["FJ", "679", "Fiji"],
  ["FI", "358", "Finland"], ["FR", "33", "France"], ["GF", "594", "French Guiana"],
  ["PF", "689", "French Polynesia"], ["GA", "241", "Gabon"], ["GM", "220", "Gambia"],
  ["GE", "995", "Georgia"], ["DE", "49", "Germany"], ["GH", "233", "Ghana"],
  ["GI", "350", "Gibraltar"], ["GR", "30", "Greece"], ["GL", "299", "Greenland"],
  ["GD", "1", "Grenada"], ["GP", "590", "Guadeloupe"], ["GU", "1", "Guam"],
  ["GT", "502", "Guatemala"], ["GG", "44", "Guernsey"], ["GN", "224", "Guinea"],
  ["GW", "245", "Guinea-Bissau"], ["GY", "592", "Guyana"], ["HT", "509", "Haiti"],
  ["HN", "504", "Honduras"], ["HK", "852", "Hong Kong SAR China"], ["HU", "36", "Hungary"],
  ["IS", "354", "Iceland"], ["IN", "91", "India"], ["ID", "62", "Indonesia"],
  ["IR", "98", "Iran"], ["IQ", "964", "Iraq"], ["IE", "353", "Ireland"],
  ["IM", "44", "Isle of Man"], ["IL", "972", "Israel"], ["IT", "39", "Italy"],
  ["JM", "1", "Jamaica"], ["JP", "81", "Japan"], ["JE", "44", "Jersey"],
  ["JO", "962", "Jordan"], ["KZ", "7", "Kazakhstan"], ["KE", "254", "Kenya"],
  ["KI", "686", "Kiribati"], ["KW", "965", "Kuwait"], ["KG", "996", "Kyrgyzstan"],
  ["LA", "856", "Laos"], ["LV", "371", "Latvia"], ["LB", "961", "Lebanon"],
  ["LS", "266", "Lesotho"], ["LR", "231", "Liberia"], ["LY", "218", "Libya"],
  ["LI", "423", "Liechtenstein"], ["LT", "370", "Lithuania"], ["LU", "352", "Luxembourg"],
  ["MO", "853", "Macao SAR China"], ["MG", "261", "Madagascar"], ["MW", "265", "Malawi"],
  ["MY", "60", "Malaysia"], ["MV", "960", "Maldives"], ["ML", "223", "Mali"],
  ["MT", "356", "Malta"], ["MH", "692", "Marshall Islands"], ["MQ", "596", "Martinique"],
  ["MR", "222", "Mauritania"], ["MU", "230", "Mauritius"], ["YT", "262", "Mayotte"],
  ["MX", "52", "Mexico"], ["FM", "691", "Micronesia"], ["MD", "373", "Moldova"],
  ["MC", "377", "Monaco"], ["MN", "976", "Mongolia"], ["ME", "382", "Montenegro"],
  ["MS", "1", "Montserrat"], ["MA", "212", "Morocco"], ["MZ", "258", "Mozambique"],
  ["MM", "95", "Myanmar"], ["NA", "264", "Namibia"], ["NR", "674", "Nauru"],
  ["NP", "977", "Nepal"], ["NL", "31", "Netherlands"], ["NC", "687", "New Caledonia"],
  ["NZ", "64", "New Zealand"], ["NI", "505", "Nicaragua"], ["NE", "227", "Niger"],
  ["NG", "234", "Nigeria"], ["MK", "389", "North Macedonia"], ["KP", "850", "North Korea"],
  ["NO", "47", "Norway"], ["OM", "968", "Oman"], ["PK", "92", "Pakistan"],
  ["PW", "680", "Palau"], ["PS", "970", "Palestinian Territories"], ["PA", "507", "Panama"],
  ["PG", "675", "Papua New Guinea"], ["PY", "595", "Paraguay"], ["PE", "51", "Peru"],
  ["PH", "63", "Philippines"], ["PL", "48", "Poland"], ["PT", "351", "Portugal"],
  ["PR", "1", "Puerto Rico"], ["QA", "974", "Qatar"], ["RE", "262", "Réunion"],
  ["RO", "40", "Romania"], ["RU", "7", "Russia"], ["RW", "250", "Rwanda"],
  ["WS", "685", "Samoa"], ["SM", "378", "San Marino"], ["ST", "239", "São Tomé and Príncipe"],
  ["SA", "966", "Saudi Arabia"], ["SN", "221", "Senegal"], ["RS", "381", "Serbia"],
  ["SC", "248", "Seychelles"], ["SL", "232", "Sierra Leone"], ["SG", "65", "Singapore"],
  ["SX", "1", "Sint Maarten"], ["SK", "421", "Slovakia"], ["SI", "386", "Slovenia"],
  ["SB", "677", "Solomon Islands"], ["SO", "252", "Somalia"], ["ZA", "27", "South Africa"],
  ["KR", "82", "South Korea"], ["SS", "211", "South Sudan"], ["ES", "34", "Spain"],
  ["LK", "94", "Sri Lanka"], ["KN", "1", "St Kitts and Nevis"], ["LC", "1", "St Lucia"],
  ["VC", "1", "St Vincent and the Grenadines"], ["SD", "249", "Sudan"], ["SR", "597", "Suriname"],
  ["SE", "46", "Sweden"], ["CH", "41", "Switzerland"], ["SY", "963", "Syria"],
  ["TW", "886", "Taiwan"], ["TJ", "992", "Tajikistan"], ["TZ", "255", "Tanzania"],
  ["TH", "66", "Thailand"], ["TL", "670", "Timor-Leste"], ["TG", "228", "Togo"],
  ["TO", "676", "Tonga"], ["TT", "1", "Trinidad and Tobago"], ["TN", "216", "Tunisia"],
  ["TR", "90", "Türkiye"], ["TM", "993", "Turkmenistan"], ["TC", "1", "Turks and Caicos Islands"],
  ["TV", "688", "Tuvalu"], ["UG", "256", "Uganda"], ["UA", "380", "Ukraine"],
  ["AE", "971", "United Arab Emirates"], ["UY", "598", "Uruguay"], ["UZ", "998", "Uzbekistan"],
  ["VU", "678", "Vanuatu"], ["VA", "39", "Vatican City"], ["VE", "58", "Venezuela"],
  ["VN", "84", "Vietnam"], ["VG", "1", "British Virgin Islands"], ["VI", "1", "US Virgin Islands"],
  ["YE", "967", "Yemen"], ["ZM", "260", "Zambia"], ["ZW", "263", "Zimbabwe"],
];
export const DIAL = DIAL_RAW.map(([iso, dial, name]) => {
  const exact = DIAL_EXACT[iso];
  return {
    iso, dial, name, flag: flagOf(iso),
    min: exact ? exact[0] : 4,
    max: exact ? exact[1] : Math.max(4, 15 - dial.length),
  };
});
